import { onDestroy, onMount } from 'svelte';
import { writable, get, type Writable } from 'svelte/store';
import { offset } from '@src/utils/offset';
import math from '@src/utils/math';
import { parseParameter } from '@src/utils/parseparameter';
import debounced from '@src/utils/debounced';
import { onEvent } from '@src/utils/events';

/**
 * Creates and binds a visual limit to the element
 */

export const createLimit = (offset: string, color = 'green', position = 'absolute') => {
	const el = document.createElement('div');
	el.style.position = position;
	el.style.top = offset;
	el.style.left = '0';
	el.style.width = '100%';
	el.style.height = '6px';
	el.style.backgroundColor = color;
	el.style.border = '1px solid white';
	el.style.zIndex = '1000';
	document.body.appendChild(el);
	return el;
};

export function useScrollProgressOfElement({
	el = writable(null as HTMLElement | null),
	interpolation = 1,
	triggerPosition = '0vh',
	debug = false,
	onScroll: scrollCallback = () => {},
	onResize: resizeCallback = () => {},
	offsetStart = 0,
	offsetEnd = 0,
}: {
	el?: Writable<HTMLElement | null>;
	interpolation?: number;
	triggerPosition?: string;
	debug?: boolean;
	onScroll?: (progress: number) => void;
	onResize?: (bounds: DOMRect & { offsetTop: number; offsetLeft: number }) => void;
	offsetStart?: number;
	offsetEnd?: number;
} = {}) {
	if ((interpolation > 1 || interpolation < 0) && debug) {
		console.warn(
			`Invalid interpolation value. Please use a value between 0 and 1. Got ${interpolation}. Defaulting to ${interpolation < 0 ? 0 : 1}`
		);
	}
	/**
	 * Variables
	 */
	const newEl: Writable<HTMLElement | null> = writable(null);
	const triggerOffset = parseParameter(triggerPosition);

	let pastProgress = -1;
	let progressLerped = 0;
	let rafId: number | null = null;
	let domStart: HTMLDivElement | null = null;
	let domEnd: HTMLDivElement | null = null;
	let domTrigger: HTMLDivElement | null = null;
	let resizeObserver: ResizeObserver | null = null;
	let height: number = 0;
	let offsetTop: number = 0;

	/**
	 * calculates the offset of the element based on the unit
	 */
	const elementOffset = (param: { number: number }, unit: 'vh' | '%') => {
		const percent = param.number / 100;
		let base = 0;

		if (unit === 'vh') base = window.innerHeight;
		if (unit === '%') base = height ?? 0;
		else if (unit !== 'vh' && unit !== '%') console.warn(`Invalid unit ${unit}. Defaulting to 'vh'`);

		return base * percent;
	};

	/**
	 * Calculates the start and end of the element based on the offset
	 */
	const getStart = () => (offsetTop ?? 0) + window.innerHeight * offsetStart;
	const getEnd = () => (offsetTop ?? 0) + (height ?? 0) + window.innerHeight * offsetEnd;

	/**
	 * Updates the progress of the element based on the interpolation
	 */
	const updateProgress = () => {
		const element = get(el) ?? get(newEl);
		if (rafId) cancelAnimationFrame(rafId);
		// clamp interpolation to prevent NaN
		const clampedInterpolation = math.clamp(interpolation, 0, 1);
		rafId = requestAnimationFrame(() => {
			progressLerped = math.lerp(progressLerped, pastProgress, clampedInterpolation);
			if (Math.abs(progressLerped - pastProgress) < 0.0001) {
				progressLerped = pastProgress;
			} else {
				updateProgress();
			}
			if (element) {
				element.style.setProperty('--rf-scroll-progress', math.decimals(progressLerped, 4).toString() ?? 0);
			}
			updateProgress();
			scrollCallback(math.decimals(progressLerped, 4));
		});
	};

	/**
	 * Calculates the progress of the element based on the scroll position
	 */
	const onScroll = () => {
		const element = get(el) ?? get(newEl);
		const scrollY = window.scrollY;
		const start = getStart() - elementOffset(triggerOffset, 'vh');
		const end = getEnd() - elementOffset(triggerOffset, 'vh');

		const clampedScroll = math.clamp(scrollY, start, end);
		const progress = math.mapLinear(clampedScroll, start, end, 0, 1);

		if (isNaN(progress)) {
			if (debug) console.warn(`Invalid start (${start}) and end (${end}) values.`);
			return;
		}

		if (pastProgress !== progress) {
			pastProgress = progress;
			if (interpolation === 1) {
				progressLerped = progress;
				scrollCallback(math.decimals(progress, 4));
				if (element) {
					element.style.setProperty('--rf-scroll-progress', math.decimals(progress, 4).toString() ?? 0);
				}
			} else {
				updateProgress();
			}
		}
	};

	/**
	 * Recalculates the element's bounds on resize
	 */
	const resize = () => {
		const elValue = get(el) ?? get(newEl);
		if (!elValue) return;
		const result = elValue.getBoundingClientRect() as DOMRect & { offsetTop: number; offsetLeft: number };
		const scrollOffset = offset(elValue);

		height = result.height;
		offsetTop = scrollOffset.offsetTop;
		result.offsetTop = scrollOffset.offsetTop;
		result.offsetLeft = scrollOffset.offsetLeft;

		resizeCallback(result);
		onScroll();
		if (debug) {
			removeLimit();
			addLimits();
		}
	};

	const resizeDebounced = debounced(resize, 100);

	/**
	 * Initializes the element with observers and event listeners
	 */
	const init = () => {
		const elValue = get(el) ?? get(newEl);
		resizeObserver = new ResizeObserver(resize);
		if (elValue) resizeObserver.observe(elValue);
		window.addEventListener('scroll', onScroll);
		onEvent('rf-canvas-load', resizeDebounced);
		onEvent('rf-page-resize', resizeDebounced);
		resize();
		onScroll();
	};

	// Debug //

	/**
	 * Adds the visual limits to the element with the dom trigger
	 */
	const addLimits = () => {
		domTrigger = createLimit(triggerOffset.raw, 'yellow', 'fixed');
		domStart = createLimit(getStart() + 'px');
		domEnd = createLimit(getEnd() + 'px', 'blue');
	};

	/**
	 * Removes the visual limits from the element
	 */
	const removeLimit = () => {
		domTrigger?.remove();
		domEnd?.remove();
		domStart?.remove();
	};

	/**
	 * Lifecycle
	 */
	onMount(init);
	onDestroy(() => {
		window.removeEventListener('scroll', onScroll);
		resizeObserver?.disconnect();
		if (rafId) cancelAnimationFrame(rafId);

		if (debug) {
			removeLimit();
		}
	});
}
